import { useCallback, useState } from "react";
import { Group, Badge, Text, Box, MultiSelect } from "@mantine/core";

interface IcdCodeRowProps {
  specimen: any;
  currentCodes: string[];
  options: { value: string; label: string }[];
  isReadOnly?: boolean;
  onCodesChange?: (specimenId: string, codes: string[]) => void;
}

export function IcdCodeRow({
  specimen,
  currentCodes,
  options,
  isReadOnly,
  onCodesChange,
}: IcdCodeRowProps) {
  const [editing, setEditing] = useState(false);

  const handleCodesChange = useCallback(
    (codes: string[]) => {
      onCodesChange?.(specimen.id, codes);
    },
    [onCodesChange, specimen.id]
  );

  const handleBlur = useCallback(() => setEditing(false), []);

  const handleStartEditing = useCallback(() => setEditing(true), []);

  const display = currentCodes.join(", ");

  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Badge
        size="xs"
        variant="dot"
        color="teal"
        style={{ flexShrink: 0, fontFamily: "monospace" }}
      >
        {specimen.number || "?"}
      </Badge>
      <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
        {specimen.source?.name || specimen.sourceName || ""}
      </Text>
      {isReadOnly ? (
        <Badge
          size="xs"
          variant="outline"
          color={display ? "teal" : "orange"}
          style={{ flexShrink: 0 }}
        >
          {display || "—"}
        </Badge>
      ) : editing ? (
        <Box style={{ flexShrink: 0, width: 180 }}>
          <MultiSelect
            size="xs"
            data={options}
            value={currentCodes}
            onChange={handleCodesChange}
            onBlur={handleBlur}
            placeholder={currentCodes.length === 0 ? "Select ICD" : undefined}
            searchable
            clearable
            autoFocus
            comboboxProps={{ withinPortal: true, position: "bottom-end" }}
            styles={{ input: { fontSize: 11 } }}
          />
        </Box>
      ) : (
        <Badge
          size="xs"
          variant="outline"
          color={display ? "teal" : "orange"}
          style={{ flexShrink: 0, cursor: "pointer" }}
          onClick={handleStartEditing}
          title="Click to edit"
        >
          {display || (
            <span style={{ fontStyle: "italic", opacity: 0.6 }}>None</span>
          )}
        </Badge>
      )}
    </Group>
  );
}
